/**
 * Mobile Form Responses Container
 * 
 * Complete responsive form responses view for mobile
 * Handles card list, detail sheet, bulk delete, and data fetching
 */

import React, { useState, useCallback } from 'react';
import { Download } from 'lucide-react';
import { useFormResponses, useFormResponseDetail } from './hooks/useFormResponses';
import MobileFormResponsesTable from './table/MobileFormResponsesTable';
import { MobileFormResponseDetailSheet } from './detail';
import { useAuth } from '../../context/AuthContext';
import { deleteFormResponses } from '../../firebase/formResponse.service';
import { exportFormResponsesCsv } from './utils/exportCsv';
import Toast from '../Toast/Toast';

const MobileFormResponsesContainer = ({
    propertyId = null, // If provided, filter by property
    fieldSchema = null,
    title = 'Form Responses',
    onResponseSelected = null,
    propertiesMap = {},
    portfolioName = '',
}) => {
    const { user, loading: authLoading } = useAuth();
    const ownerId = user?.uid;

    console.log('[MobileFormResponsesContainer] Auth state:', {
        user: !!user,
        authLoading,
        ownerId: ownerId,
        userEmail: user?.email,
    });

    // Fetch responses
    const { responses, loading, error } = useFormResponses(ownerId, {
        propertyId,
        type: propertyId ? 'property' : 'owner',
    });

    // Detail view management
    const detail = useFormResponseDetail();

    // Selection state for bulk/single delete
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [toast, setToast] = useState(null);

    // Bulk delete handler
    const handleBulkDeleteRequest = useCallback(() => {
        if (selectedIds.length === 0) return;
        setDeleteConfirm({ type: 'bulk', ids: [...selectedIds] });
    }, [selectedIds]);

    // Single delete handler
    const handleSingleDeleteRequest = useCallback((id) => {
        setDeleteConfirm({ type: 'single', id });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!deleteConfirm) return;
        const idsToDelete = deleteConfirm.type === 'bulk' ? deleteConfirm.ids : [deleteConfirm.id];
        setIsDeleting(true);
        try {
            const result = await deleteFormResponses(idsToDelete);
            if (result.success) {
                setToast({ type: 'success', message: `Deleted ${result.deletedCount} response${result.deletedCount > 1 ? 's' : ''}` });
                if (deleteConfirm.type === 'bulk') setSelectedIds([]);
            } else {
                setToast({ type: 'error', message: result.error || 'Failed to delete' });
            }
        } catch (err) {
            setToast({ type: 'error', message: err.message || 'Failed to delete' });
        } finally {
            setDeleteConfirm(null);
            setIsDeleting(false);
        }
    }, [deleteConfirm]);

    const handleDismissToast = useCallback(() => setToast(null), []);

    // Create a map of raw responses by ID for detail view
    const rawResponseMap = React.useMemo(() => {
        return responses.reduce((map, response) => {
            map[response.id] = response;
            return map;
        }, {});
    }, [responses]);

    // Handle row click - pass the RAW response, not the formatted one
    const handleRowClick = (formattedResponse) => {
        const rawResponse = rawResponseMap[formattedResponse.id];
        detail.open(rawResponse || formattedResponse);
        onResponseSelected?.(formattedResponse);
    };

    // Export CSV — uses ALL responses
    const handleExportCSV = useCallback(() => {
        const result = exportFormResponsesCsv(responses, { propertiesMap, portfolioName });
        if (!result.success) {
            setToast({ type: 'error', message: 'No form responses to export' });
        } else {
            setToast({ type: 'success', message: `Exported ${result.count} response${result.count > 1 ? 's' : ''} to CSV` });
        }
    }, [responses, propertiesMap, portfolioName]);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-base font-semibold text-notion-text">{title}</h2>
                </div>
                <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-1 px-3 py-1.5 border border-notion-border bg-white text-stone-600 rounded-lg text-xs font-medium shadow-sm"
                >
                    <Download className="w-3.5 h-3.5" /> CSV
                </button>
            </div>

            {/* Auth Loading State */}
            {authLoading && (
                <div className="bg-white border border-notion-border rounded-lg overflow-hidden">
                    <div className="p-8 text-center">
                        <div className="inline-block">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-stone-700 mx-auto mb-3"></div>
                            <p className="text-sm text-stone-600">Initializing...</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Card List */}
            {!authLoading && (
                <MobileFormResponsesTable
                    responses={responses}
                    loading={loading}
                    error={error}
                    onRowClick={handleRowClick}
                    propertiesMap={propertiesMap}
                    portfolioName={portfolioName}
                    selectedIds={selectedIds}
                    onSelectionChange={setSelectedIds}
                    onBulkDelete={handleBulkDeleteRequest}
                    onSingleDelete={handleSingleDeleteRequest}
                />
            )}

            {/* Detail Sheet */}
            {!authLoading && (
                <MobileFormResponseDetailSheet
                    response={detail.response}
                    isOpen={detail.isOpen}
                    onClose={detail.close}
                    fieldSchema={fieldSchema}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-4">
                        <h3 className="text-sm font-semibold text-stone-900">
                            {deleteConfirm.type === 'bulk'
                                ? `Delete ${deleteConfirm.ids.length} response${deleteConfirm.ids.length > 1 ? 's' : ''}?`
                                : 'Delete this response?'}
                        </h3>
                        <p className="text-xs text-stone-500">This action cannot be undone.</p>
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                disabled={isDeleting}
                                className="px-3 py-1.5 text-xs font-medium text-stone-600 border border-notion-border rounded hover:bg-stone-50 transition disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition disabled:opacity-50"
                            >
                                {isDeleting ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast notification */}
            {toast && <Toast message={toast.message} type={toast.type} onClose={handleDismissToast} />}
        </div>
    );
};

export default MobileFormResponsesContainer;
